/* Shared game types. Pure data — no DOM, no audio. */

export const TOPPINGS = [
  'smothered',
  'covered',
  'chunked',
  'diced',
  'peppered',
  'capped',
  'topped',
  'country',
] as const;
export type Topping = (typeof TOPPINGS)[number];

export const WAFFLE_KINDS = ['classic', 'pecan'] as const;
export type WaffleKind = (typeof WAFFLE_KINDS)[number];

export const EGG_STYLES = ['scrambled', 'overEasy'] as const;
export type EggStyle = (typeof EGG_STYLES)[number];

export const ITEM_KINDS = ['hashBrowns', 'waffle', 'eggs', 'bacon'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export interface HashBrownSpec {
  /** Always stored in canonical lingo order. */
  toppings: Topping[];
  /** "Scattered well" — the customer wants them crispy. */
  well: boolean;
}

export interface OrderSpec {
  hashBrowns?: HashBrownSpec;
  waffle?: WaffleKind;
  eggs?: EggStyle;
  bacon?: true;
}

export type Doneness = 'raw' | 'golden' | 'crispy' | 'burnt';

export interface PlatedItem {
  kind: ItemKind;
  /** 0 … 1, where 1 is exactly what the customer ordered. */
  quality: number;
  /** Human-readable complaints ("forgot the onions"). */
  issues: string[];
}

export type TicketTag = 'usual' | 'proposal' | 'host' | 'streamer' | 'eggKaren' | 'fema' | 'calmed';

export interface Ticket {
  id: number;
  /** Number printed on the ticket (resets never; it's a long night). */
  no: number;
  seatId: number;
  customerId: number;
  order: OrderSpec;
  plated: Partial<Record<ItemKind, PlatedItem>>;
  createdAt: number;
  /** Colour slot used to tag stations cooking for this ticket. */
  color: number;
  tags: TicketTag[];
  /** "The usual" hides the order until this sim time. */
  revealAt: number;
  status: 'open' | 'served' | 'cancelled';
}

export type ArchetypeId =
  | 'trucker'
  | 'nurse'
  | 'college'
  | 'club'
  | 'floridaMan'
  | 'cop'
  | 'churchLady'
  | 'bachelorette'
  | 'groom'
  | 'raccoon'
  | 'philosopher'
  | 'uniformGuy'
  | 'tiredParent'
  | 'gymBro'
  | 'tourist'
  | 'regular'
  | 'weatherman'
  | 'fema'
  | 'streamer'
  | 'host'
  | 'couple'
  | 'eggKaren'
  | 'fighter'
  | 'returning';

export type HairStyle =
  'short' | 'long' | 'bald' | 'bun' | 'afro' | 'mohawk' | 'ponytail' | 'mullet' | 'curly' | 'buzz';
export type HatStyle =
  | 'none'
  | 'trucker'
  | 'cowboy'
  | 'church'
  | 'beanie'
  | 'cap'
  | 'tiara'
  | 'hood'
  | 'headband'
  | 'visor';
export type EyeStyle = 'normal' | 'sunglasses' | 'glasses' | 'sleepy' | 'mask' | 'glow';
export type FacialHair = 'none' | 'beard' | 'mustache' | 'stubble';
export type Accessory =
  | 'none'
  | 'sash'
  | 'bowtie'
  | 'badge'
  | 'phone'
  | 'lanyard'
  | 'baby'
  | 'nametag'
  | 'camera'
  | 'mic'
  | 'femaJacket'
  | 'raincoat'
  | 'chain'
  | 'suspenders';
export type ShirtStyle =
  | 'plain'
  | 'flannel'
  | 'stripes'
  | 'hawaiian'
  | 'tank'
  | 'hoodie'
  | 'scrubs'
  | 'suit'
  | 'sparkle'
  | 'turtleneck'
  | 'polo';

export interface CharacterLook {
  species: 'human' | 'raccoon' | 'host';
  skin: string;
  hair: string;
  hairStyle: HairStyle;
  hat: HatStyle;
  hatColor: string;
  eyes: EyeStyle;
  facialHair: FacialHair;
  shirt: string;
  shirtAlt: string;
  shirtStyle: ShirtStyle;
  accessory: Accessory;
}

export type CustomerState = 'entering' | 'waiting' | 'eating' | 'leaving' | 'gone';

export interface Customer {
  id: number;
  archetype: ArchetypeId;
  name: string;
  seatId: number;
  look: CharacterLook;
  state: CustomerState;
  stateTime: number;
  /** 0 … 1; walks out at 0. */
  patience: number;
  /** Seconds of patience at full meter. */
  patienceSeconds: number;
  /** 0 … 1 coffee level; only counter customers drink coffee. */
  mug: number;
  drinksCoffee: boolean;
  coffeeOnly: boolean;
  ticketId: number | null;
  tipMult: number;
  angry: boolean;
  /** Seconds they'll sit with only coffee before leaving happily. */
  coffeeVisitLeft: number;
}

export interface Seat {
  id: number;
  kind: 'stool' | 'booth';
  /** Position within its row (stool 0…5, booth 0…2); the renderer maps it to pixels. */
  index: number;
  customerId: number | null;
}

export interface GrillSlot {
  id: number;
  ticketId: number | null;
  /** Seconds cooked. */
  time: number;
  toppings: Topping[];
  /** Seconds spent burnt (drives the smoke alarm). */
  smokeTime: number;
  alarmed: boolean;
}

export interface WaffleIron {
  id: number;
  ticketId: number | null;
  kind: WaffleKind | null;
  time: number;
  smokeTime: number;
  alarmed: boolean;
  dinged: boolean;
}

export interface LineJob {
  ticketId: number;
  kind: 'eggs' | 'bacon';
  eggStyle: EggStyle | null;
  time: number;
  duration: number;
}

export type IndexLevel = 'green' | 'yellow' | 'red';

export type ChairOutcome = 'caught' | 'perfect' | 'whiff' | 'hit';

export interface ChairState {
  /** Seconds since thrown (real time — the kitchen slows down, the chair doesn't). */
  t: number;
  duration: number;
  windowStart: number;
  perfectAt: number;
  perfectTolerance: number;
  /** Ignore taps this early so a mid-cook tap doesn't count as a whiff. */
  grace: number;
  /** Target x on the counter (scene px) — where it lands. */
  targetX: number;
  spin: number;
  outcome: ChairOutcome | null;
  /** Seconds since resolved (for the freeze-frame / crash animation). */
  resolvedT: number;
}

export type FightPhase = 'argue' | 'round' | 'fight' | 'ko';

export interface FightState {
  phase: FightPhase;
  t: number;
  topic: string;
  fighters: [string, string];
  looks: [CharacterLook, CharacterLook];
  hp: [number, number];
  chairTimes: number[];
  chairsThrown: number;
  fightDuration: number;
  servedDuring: number;
  flawless: boolean;
  round: number;
}

export interface StormState {
  name: string;
  t: number;
  duration: number;
  outages: { at: number; length: number }[];
  minIndex: number;
  verdict: 'green' | 'yellow' | 'red' | null;
  femaCustomerId: number | null;
  nextThunder: number;
}

export interface HostState {
  t: number;
  ticketId: number;
  deadline: number;
  resolved: 'pleased' | 'tolerant' | 'disappointed' | null;
  resolvedT: number;
}

export interface LiveState {
  customerId: number;
  t: number;
  duration: number;
  viewers: number;
}

export type Mode = 'daily' | 'free';
export type DifficultyId = 'trainee' | 'standard' | 'boss';

export interface GameStats {
  served: number;
  perfect: number;
  walkouts: number;
  wrong: number;
  hashBrowns: number;
  waffles: number;
  toppings: number;
  burnt: number;
  chairsThrown: number;
  chairsCaught: number;
  perfectCatches: number;
  unbothered: number;
  coffeeRefills: number;
  views: number;
  bestCombo: number;
  allTheWay: number;
  alarms: number;
  hourlyIndex: number[];
  fightsSurvived: number;
}

export type EndReason = 'shiftComplete' | 'closed';

export interface GameResult {
  reason: EndReason;
  tips: number;
  index: number;
  stats: GameStats;
  hostPleased: boolean;
  femaVerdict: StormState['verdict'];
  stormName: string | null;
  mode: Mode;
  difficulty: DifficultyId;
  shiftNumber: number;
  endedAtMinutes: number;
  proposalYes: boolean | null;
  secretShopper: boolean;
  sawSirThisIsAWaffleHouse: boolean;
  raccoonServed: boolean;
  /** Customers calmed at the shift change before this shift (null if there wasn't one). */
  calmedBefore: number | null;
}

export type SpeakerId = number | 'dottie' | 'fighterA' | 'fighterB' | 'crowd';

export type BannerStyle = 'info' | 'fight' | 'ko' | 'danger' | 'host' | 'good' | 'storm' | 'sleep';

export type SfxName =
  | 'drop'
  | 'topping'
  | 'scrape'
  | 'plate'
  | 'orderUp'
  | 'ding'
  | 'pour'
  | 'coffee'
  | 'cash'
  | 'perfect'
  | 'error'
  | 'door'
  | 'walkout'
  | 'whoosh'
  | 'catch'
  | 'crash'
  | 'bell'
  | 'fightBell'
  | 'thunder'
  | 'powerDown'
  | 'powerUp'
  | 'alarm'
  | 'glitch'
  | 'toss'
  | 'crowdOoh'
  | 'hearts'
  | 'select'
  | 'burn'
  | 'eggs'
  | 'bacon'
  | 'snore';

export type GameEvent =
  | { type: 'ticketCreated'; ticket: Ticket; call: string; speak: string }
  | { type: 'ticketRevealed'; ticket: Ticket }
  | {
      type: 'ticketServed';
      ticket: Ticket;
      quality: number;
      perfect: boolean;
      tip: number;
      combo: number;
      multiplier: number;
    }
  | { type: 'ticketCancelled'; ticket: Ticket; reason: 'walkout' | 'fight' | 'selfServe' | 'host' }
  | { type: 'ticketSelected'; ticketId: number | null }
  | { type: 'customerArrived'; customer: Customer }
  | { type: 'customerLeft'; customer: Customer; angry: boolean }
  | {
      type: 'speech';
      speaker: SpeakerId;
      text: string;
      duration: number;
      mood?: 'normal' | 'happy' | 'angry' | 'spooky';
    }
  | { type: 'itemPlated'; ticketId: number; kind: ItemKind; quality: number; issues: string[] }
  | { type: 'itemStarted'; ticketId: number; kind: ItemKind }
  | { type: 'burnt'; station: 'grill' | 'iron'; id: number }
  | { type: 'tossed'; station: 'grill' | 'iron'; id: number }
  | { type: 'alarm'; station: 'grill' | 'iron'; id: number }
  | {
      type: 'indexChanged';
      value: number;
      delta: number;
      level: IndexLevel;
      previousLevel: IndexLevel;
    }
  | { type: 'tip'; amount: number; seatId: number; label: string }
  | { type: 'chairThrown'; chair: ChairState }
  | { type: 'chairResolved'; outcome: ChairOutcome; collateral: string | null }
  | { type: 'fightPhase'; phase: FightPhase; fight: FightState }
  | { type: 'fightEnded'; fight: FightState }
  | { type: 'banner'; text: string; sub?: string; style: BannerStyle; duration: number }
  | { type: 'headline'; text: string; breaking?: boolean }
  | { type: 'power'; on: boolean }
  | { type: 'storm'; active: boolean; name: string }
  | { type: 'host'; phase: 'start' | 'end'; outcome?: HostState['resolved'] }
  | { type: 'live'; active: boolean; customerId: number }
  | { type: 'coffee'; customerId: number; seatId: number }
  | { type: 'hour'; hourIndex: number; label: string }
  | { type: 'jukebox'; song: string }
  | { type: 'achievement'; id: string }
  | { type: 'hearts'; seatId: number; yes: boolean }
  | { type: 'selfServe'; customerId: number }
  | { type: 'sfx'; name: SfxName }
  | { type: 'over'; result: GameResult };

export type ActionResult = { ok: true } | { ok: false; reason: string };
